import stringify from 'json-stringify-safe';
import type { IHttpError } from '@wix/http-client';
import { retry } from '@wix/pricing-plans-utils/http';
import type { PlatformControllerFlowAPI, ControllerFlowAPI, EditorScriptFlowAPI } from '@wix/yoshi-flow-editor';
import { HttpClient, ControllerParams } from '@wix/yoshi-flow-editor';
import { PanoramaClient } from '@wix/yoshi-flow-editor/build/cjs/exports/panorama';

export const toError = (e: unknown): Error =>
  e instanceof Error ? e : new Error(typeof e === 'string' ? e : stringify(e));

export const getApplicationErrorCode = (e: any) => e?.response?.data?.details?.applicationError?.code;

export const getApplicationErrorData = (e: unknown): Record<string, unknown> | undefined => {
  if (HttpClient.isHttpError(e)) {
    return e.response?.data.details?.applicationError?.data;
  }

  return undefined;
};

export function isWixHttpError(error: unknown): error is IHttpError {
  return Boolean(error && error instanceof Error && (error as any).isWixHttpError);
}

export const isNetworkError = (e: unknown) => isWixHttpError(e) && e.message === 'Network Error';
export const is403 = (e: unknown) => isWixHttpError(e) && e.response?.status === 403;
export const is401 = (e: unknown) => isWixHttpError(e) && e.response?.status === 401;

export async function ignoreErrors<T>(action: () => T | Promise<T>) {
  try {
    return await action();
  } catch {}
}

type Predicate = (e: unknown, flowAPI: ControllerFlowAPI | PlatformControllerFlowAPI) => boolean;
type Options = Parameters<ReturnType<PanoramaClient['errorMonitor']>['reportError']>;
type ExtendedOptions = Options[1] & { additionalIgnorePredicates?: Predicate[] };

const isEditor403: Predicate = (e, flowAPI) => flowAPI.environment.isEditor && is403(e);
const DEFAULT_IGNORED_ERROR_PREDICATES: Predicate[] = [isNetworkError, isEditor403];

export function captureViewerException(
  flowAPI: ControllerFlowAPI | PlatformControllerFlowAPI,
  error: unknown,
  extendedOptions: ExtendedOptions = {},
) {
  const { additionalIgnorePredicates = [], ...options } = extendedOptions;
  const isErrorIgnored = [...additionalIgnorePredicates, ...DEFAULT_IGNORED_ERROR_PREDICATES].some((isIgnored) =>
    isIgnored(error, flowAPI),
  );
  if (!isErrorIgnored) {
    flowAPI.panoramaClient?.errorMonitor().reportError(toError(error), options);
  }
}

enum Severity {
  Fatal = 'fatal',
  Error = 'error',
  Warning = 'warning',
  Log = 'log',
  Info = 'info',
  Debug = 'debug',
  Critical = 'critical',
}

interface CaptureExceptionOptions {
  print: boolean;
  interactionTag: string;
}

export function captureEditorException(
  flowAPI: EditorScriptFlowAPI,
  err: unknown,
  options: Partial<CaptureExceptionOptions> = {},
) {
  if (typeof err === 'object' && err !== null && !(err instanceof Error)) {
    flowAPI.panoramaClient?.errorMonitor().addBreadcrumb({
      level: Severity.Error,
      message: 'Error object.',
      data: err,
    });
  }
  flowAPI.panoramaClient?.errorMonitor().reportError(
    toError(err),
    options.interactionTag
      ? {
          tags: {
            interaction: options.interactionTag,
          },
        }
      : {},
  );
  if (options.print) {
    console.error(err);
  }
}

export async function retry401<T>(
  wixCodeApi: ControllerParams['controllerConfig']['wixCodeApi'],
  flowAPI: ControllerParams['flowAPI'] | PlatformControllerFlowAPI,
  action: () => Promise<T>,
) {
  return retry(
    {
      times: 1,
      delay: 20,
      refresh: () => wixCodeApi.site.loadNewSession(),
      cancelRetryIf(e) {
        return !is401(e);
      },
    },
    async () => {
      try {
        const result = await action();
        return result;
      } catch (error: unknown) {
        // mark the error as resolved
        flowAPI.errorHandler.getResolvedError(error);
        throw error;
      }
    },
  );
}
